import type { FetchFn } from '../client.js';
import {
  createAppDirectAuth,
  AuthError,
  type AppDirectAuthConfig,
} from '../index.js';
import type { CookieMutation } from '../types.js';
import { appHomeUrl, errorRedirectUrl } from './redirects.js';
import {
  applyCookieMutations,
  clearCookieMutations,
  clearCookiesOnResponse,
  formatSetCookieHeader,
  type CookieHeaderWriter,
  readAuthCookies,
} from './shared.js';
import {
  assertSameSiteRequest,
  parseRequestBody,
  readBodySessionTokens,
  type SameSiteRequestHeaders,
} from './same-site.js';

export {
  applyCookieMutations,
  formatSetCookieHeader,
  readAuthCookies,
  type CookieHeaderWriter,
};
export { buildSessionCookies } from '../session/cookies.js';
export {
  assertSameSiteRequest,
  getRequestOrigin,
  parseRequestBody,
  readBodySessionTokens,
  type SameSiteRequestHeaders,
} from './same-site.js';

export interface HandlerRequest {
  method?: string;
  body?: unknown;
  headers: SameSiteRequestHeaders & { cookie?: string };
  query: Record<string, string | string[] | undefined>;
}

export interface HandlerResponse {
  status(code: number): HandlerResponse;
  json(body: unknown): void;
  redirect(status: number, url: string): void;
  setHeader(name: string, value: string | string[]): void;
  appendHeader?(name: string, value: string): void;
  end(): void;
}

function queryParam(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

export function createHandlerCookieWriter(res: HandlerResponse): CookieHeaderWriter {
  return {
    setHeader: (name, value) => res.setHeader(name, value),
    appendHeader: res.appendHeader
      ? (name, value) => res.appendHeader!(name, value)
      : undefined,
  };
}

function applyCookies(res: HandlerResponse, mutations: CookieMutation[]): void {
  applyCookieMutations(createHandlerCookieWriter(res), mutations);
}

export function createAuthRouteHandlers(
  config: AppDirectAuthConfig,
  options?: { fetch?: FetchFn },
) {
  const auth = createAppDirectAuth(config, options);

  return {
    login(req: HandlerRequest, res: HandlerResponse) {
      if (req.method !== 'GET') {
        res.status(405).json({ error: 'Method not allowed' });
        return;
      }

      try {
        const result = auth.buildLoginRedirect();
        applyCookies(res, result.cookiesToSet);
        res.redirect(302, result.authorizeUrl);
      } catch (err) {
        const code = err instanceof AuthError ? err.code : 'server_config';
        res.status(500).json({ error: code });
      }
    },

    async callback(req: HandlerRequest, res: HandlerResponse) {
      if (req.method !== 'GET') {
        res.status(405).end();
        return;
      }

      const { store } = readAuthCookies(req.headers.cookie);
      const code = queryParam(req.query.code);
      const error = queryParam(req.query.error);
      const state = queryParam(req.query.state);

      if (error || !code) {
        res.redirect(302, errorRedirectUrl(auth.config, error ?? 'no_code'));
        return;
      }

      try {
        const result = await auth.handleCallback({ code, state, cookies: store });
        applyCookies(res, [
          ...result.cookiesToSet,
          ...clearCookieMutations(auth.config, result.cookiesToClear),
        ]);
        res.redirect(302, appHomeUrl(auth.config));
      } catch (err) {
        const code = err instanceof AuthError ? err.code : 'server_config';
        res.redirect(302, errorRedirectUrl(auth.config, code));
      }
    },

    async me(req: HandlerRequest, res: HandlerResponse) {
      if (req.method !== 'GET') {
        res.status(405).json({ error: 'Method not allowed' });
        return;
      }

      const { session, refreshToken } = readAuthCookies(req.headers.cookie);
      const result = await auth.getMe({ sessionToken: session, refreshToken });
      if (result.cookiesToSet?.length) {
        applyCookies(res, result.cookiesToSet);
      }
      res.status(200).json({
        user: result.user,
        hasSession: result.hasSession,
      });
    },

    async jwt(req: HandlerRequest, res: HandlerResponse) {
      if (req.method !== 'GET') {
        res.status(405).json({ error: 'Method not allowed' });
        return;
      }

      const { session, refreshToken } = readAuthCookies(req.headers.cookie);

      try {
        const result = await auth.getFeJwt({ sessionToken: session, refreshToken });
        if (result.cookiesToSet?.length) {
          applyCookies(res, result.cookiesToSet);
        }
        res.status(200).json({ token: result.token });
      } catch (err) {
        if (err instanceof AuthError) {
          res.status(err.statusCode).json({
            error: err.code,
            token: null,
            refresh_url: auth.config.routes.refresh,
          });
          return;
        }
        res.status(500).json({ error: 'server_config', token: null });
      }
    },

    async refresh(req: HandlerRequest, res: HandlerResponse) {
      if (req.method !== 'GET' && req.method !== 'POST') {
        res.status(405).json({ error: 'Method not allowed' });
        return;
      }

      const { refreshToken } = readAuthCookies(req.headers.cookie);
      if (!refreshToken) {
        res.status(401).json({ error: 'not_authenticated', ok: false });
        return;
      }

      try {
        const result = await auth.refreshSession({ refreshToken });
        applyCookies(res, result.cookiesToSet);
        res.status(200).json({ ok: true, token: result.fejwt });
      } catch {
        res.status(502).json({ error: 'refresh_failed', ok: false });
      }
    },

    logout(req: HandlerRequest, res: HandlerResponse) {
      if (req.method !== 'GET' && req.method !== 'POST') {
        res.status(405).json({ error: 'Method not allowed' });
        return;
      }

      clearCookiesOnResponse(auth.config, createHandlerCookieWriter(res));
      res.redirect(302, appHomeUrl(auth.config));
    },

    async importSession(req: HandlerRequest, res: HandlerResponse) {
      if (req.method !== 'POST') {
        res.status(405).json({ error: 'Method not allowed' });
        return;
      }
      if (!assertSameSiteRequest(req.headers, auth.config.appBaseUrl)) {
        res.status(403).json({ error: 'Cross-site request blocked' });
        return;
      }

      const tokens = readBodySessionTokens(parseRequestBody(req.body));
      if (!tokens) {
        res.status(400).json({ error: 'invalid_body' });
        return;
      }

      try {
        const result = await auth.importSession(tokens);
        applyCookies(res, result.cookiesToSet);
        res.status(200).json({ ok: true });
      } catch (err) {
        if (err instanceof AuthError) {
          res.status(err.statusCode).json({ error: err.code });
          return;
        }
        res.status(500).json({ error: 'server_config' });
      }
    },

    auth,
  };
}

export type { AppDirectAuthConfig };
